import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen, Rocket, Key, Package, Gift, ShoppingBag, Briefcase, Keyboard, Code2,
  SlidersHorizontal, Terminal, LifeBuoy, RefreshCw, Copy, Check, Lock, Info, AlertTriangle, ArrowLeft,
  Smartphone, Music,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth, API } from "@/App";
import { tokenStore } from "@/lib/token";

// Documentation of the VantaPhone FiveM script, shown in the dashboard to
// the buyers of any VantaPhone product (Yphone, Tablette, Pack).

const PRODUCT_LABELS = {
  "vantaphone-yphone": "Yphone 18 Pro",
  "vantaphone-tablette": "Tablette",
  "vantaphone-pack": "Pack complet",
};

function CodeBlock({ code, lang = "lua" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copié");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative group my-4">
      <div className="flex items-center justify-between px-3 py-1.5 bg-black/40 border border-white/5 border-b-0 rounded-t-lg">
        <span className="text-[11px] uppercase tracking-wider text-white/30">{lang}</span>
        <button onClick={copy} className="text-white/40 hover:text-white p-1" title="Copier">
          {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <pre className="bg-black/30 border border-white/5 rounded-b-lg p-4 overflow-x-auto text-[13px] leading-relaxed text-blue-100/90 font-mono">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function Note({ type = "info", children }) {
  const styles = type === "warn"
    ? "bg-amber-500/10 border-amber-500/25 text-amber-200"
    : "bg-blue-500/10 border-blue-500/25 text-blue-100";
  const Icon = type === "warn" ? AlertTriangle : Info;
  return (
    <div className={`my-4 flex gap-3 border rounded-lg p-3 text-sm ${styles}`}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="space-y-1">{children}</div>
    </div>
  );
}

const C = ({ children }) => <code className="px-1.5 py-0.5 rounded bg-white/10 text-[0.85em] font-mono text-blue-100">{children}</code>;
const P = ({ children }) => <p className="text-sm text-white/70 leading-relaxed my-3">{children}</p>;
const H3 = ({ children }) => <h3 className="text-base font-semibold text-white mt-6 mb-2">{children}</h3>;
const UL = ({ children }) => <ul className="list-disc pl-5 space-y-1.5 text-sm text-white/70 my-3">{children}</ul>;
const Steps = ({ items }) => (
  <ol className="space-y-3 my-4">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3">
        <span className="w-6 h-6 rounded-full bg-blue-500/15 text-blue-300 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
        <div className="text-sm text-white/70 leading-relaxed">{item}</div>
      </li>
    ))}
  </ol>
);
const Table = ({ head, rows }) => (
  <div className="overflow-x-auto my-4 border border-white/5 rounded-lg">
    <table className="w-full text-sm">
      <thead className="bg-white/5">
        <tr>{head.map((h) => <th key={h} className="text-left font-medium text-white/80 px-3 py-2">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-t border-white/5">
            {row.map((cell, j) => <td key={j} className="px-3 py-2 text-white/70 align-top">{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SECTIONS = [
  {
    id: "demarrage",
    title: "Démarrage rapide",
    icon: Rocket,
    body: (
      <>
        <P>Le script fonctionne avec ESX, QBCore, ox_core ou sans framework. Il a besoin d'<C>oxmysql</C>.</P>
        <Steps items={[
          <>Télécharge le zip depuis ton tableau de bord et ouvre-le. Tu y trouves un dossier <C>[vantaphone]</C>.</>,
          <>Copie ce dossier dans le dossier <C>resources</C> de ton serveur. <b>Ne renomme aucun dossier</b> : FiveM utilise le nom du dossier comme nom de ressource, et un appareil mal nommé ne s'ouvre pas (un message rouge l'indique dans la console).</>,
          <>Importe <C>vantaphone-core/vantaphone.sql</C> dans ta base de données (HeidiSQL, phpMyAdmin...). Tu peux le réimporter à chaque mise à jour, il ne touche pas aux données existantes.</>,
          <>Ajoute les ressources dans ton <C>server.cfg</C>, après <C>oxmysql</C> et ton framework (voir ci-dessous).</>,
          <>Colle ta clé de licence dans <C>vantaphone-core/config/config.lua</C> (voir « Licence »).</>,
          <>Ajoute les objets à ton inventaire (voir « Objets d'inventaire »), puis redémarre le serveur.</>,
        ]} />
        <CodeBlock lang="cfg" code={`# vantaupdate : droit d'installer les mises à jour (sandbox FiveM)
add_filesystem_permission vantaphone-core write vantaphone-core
add_filesystem_permission vantaphone-core write vantaphone-yphone
add_filesystem_permission vantaphone-core write vantaphone-tpadlette
add_filesystem_permission vantaphone-core write vantaphone-exemple-app

ensure oxmysql
# ton framework (es_extended / qb-core / ox_core) et ton inventaire avant

ensure vantaphone-core
ensure vantaphone-yphone      # si tu as l'Yphone
ensure vantaphone-tpadlette   # si tu as la tablette
ensure vantaphone-exemple-app # facultatif : exemple d'apps perso`} />
        <Note>Tu n'as acheté qu'un appareil ? Ne démarre que celui-là : les autres dossiers ne sont pas dans ton zip.</Note>
        <Note>Tu avais l'ancien Tphone (ou le Sphone) ? Remplace une fois le dossier <C>vantaphone-tphone</C> par le dossier <C>vantaphone-yphone</C> du zip (supprime aussi <C>vantaphone-sphone</C>), remplace <C>vantaphone-tphone</C> par <C>vantaphone-yphone</C> dans <C>server.cfg</C>, ajoute l'objet <C>yphone</C> à ton inventaire, puis redémarre le serveur. Tes joueurs gardent toutes leurs données : leur ancien téléphone devient un Yphone.</Note>
        <Note>Les lignes <C>add_filesystem_permission</C> permettent à <C>vantaupdate</C> d'installer les mises à jour : les serveurs FiveM récents interdisent sinon aux ressources d'écrire des scripts (<C>.lua</C>).</Note>
        <Note>Tout marche sans aucune clé, musique en entier comprise. Facultatif : relier son vrai compte Spotify (Client ID). Voir « Spotifi » et « Autres réglages &gt; Clés du server.cfg ».</Note>
      </>
    ),
  },
  {
    id: "licence",
    title: "Licence",
    icon: Key,
    body: (
      <>
        <P>Ta clé (<C>VP-XXXX-XXXX-XXXX-XXXX</C>) est affichée sur la carte du produit dans ton tableau de bord. Colle-la sur la première ligne de <C>vantaphone-core/config/config.lua</C> :</P>
        <CodeBlock code={`Config.License = 'VP-XXXX-XXXX-XXXX-XXXX'

-- Plusieurs produits achetés séparément (ex. Yphone + Tablette) :
Config.License = 'VP-AAAA-BBBB-CCCC-DDDD, VP-EEEE-FFFF-GGGG-HHHH'`} />
        <UL>
          <li>Au démarrage, puis toutes les heures, le script vérifie la clé sur VantaPulse et n'active que les appareils achetés : l'Yphone, la tablette, ou les deux avec le Pack. Une ancienne licence Sphone débloque l'Yphone.</li>
          <li>La console du serveur affiche le résultat : <C>Appareils activés : Yphone, ...</C>. Tape <C>vantalicense</C> dans la console pour revérifier tout de suite.</li>
          <li>Une clé ne marche que sur <b>un seul serveur</b>. La première activation la lie à ce serveur.</li>
          <li>Pour changer d'hébergeur ou de machine : sur la carte du produit, clique sur <b>« Changer d'appareil »</b>, puis démarre le nouveau serveur.</li>
          <li>Abonnement mensuel : s'il n'est pas renouvelé, les appareils se verrouillent à la vérification suivante. À vie : rien à faire.</li>
          <li>Si le site est injoignable, la dernière validation reste valable 72 h : tes joueurs ne sont pas bloqués.</li>
        </UL>
        <Table head={["Message dans la console", "Ce qu'il faut faire"]} rows={[
          ["Aucune clé renseignée", <>Remplis <C>Config.License</C> puis redémarre la ressource.</>],
          ["Clé de licence invalide", "Vérifie que tu as copié la clé en entier, sans espace en trop."],
          ["Déjà utilisée sur un autre appareil", <>Clique sur « Changer d'appareil » dans ton tableau de bord, puis tape <C>vantalicense</C>.</>],
          ["Abonnement expiré", "Renouvelle l'abonnement depuis ton tableau de bord."],
          ["Cette clé a été désactivée", "Contacte le support VantaPulse."],
        ]} />
      </>
    ),
  },
  {
    id: "objets",
    title: "Objets d'inventaire",
    icon: Package,
    body: (
      <>
        <P>Chaque appareil est un objet : <C>yphone</C> et <C>tpadlette</C> (la tablette). Les fichiers prêts à copier sont dans <C>vantaphone-core/install/</C>, et les images dans <C>vantaphone-core/install/images/</C>.</P>
        <H3>ox_inventory</H3>
        <P>Ajoute ceci dans <C>ox_inventory/data/items.lua</C> et copie les images dans <C>ox_inventory/web/images/</C> :</P>
        <CodeBlock code={`['yphone'] = {
    label = 'Yphone 18 Pro', weight = 190, stack = false, close = true,
    client = { export = 'vantaphone-core.useItem' },
},
['tpadlette'] = {
    label = 'Tpadlette Pro', weight = 480, stack = false, close = true,
    client = { export = 'vantaphone-core.useItem' },
},`} />
        <H3>QBCore</H3>
        <P>Dans <C>qb-core/shared/items.lua</C>, puis images dans <C>qb-inventory/html/images/</C> :</P>
        <CodeBlock code={`yphone    = { name = 'yphone',    label = 'Yphone 18 Pro',    weight = 190, type = 'item', image = 'yphone.png',    unique = true, useable = true, shouldClose = true, description = 'Téléphone VantaOS 27' },
tpadlette = { name = 'tpadlette', label = 'Tpadlette Pro',    weight = 480, type = 'item', image = 'tpadlette.png', unique = true, useable = true, shouldClose = true, description = 'Tablette VantaOS 27' },`} />
        <H3>ESX (inventaire de base)</H3>
        <P>Exécute <C>vantaphone-core/install/esx-items.sql</C> dans ta base. Avec ox_inventory, suis plutôt la partie ox_inventory.</P>
        <Note>Tes joueurs avaient l'ancien objet <C>tphone</C> ou <C>sphone</C> ? Garde-le dans ton inventaire le temps qu'ils se reconnectent : il est échangé contre un <C>yphone</C> quand ils sortent leur téléphone.</Note>
        <Note>Utiliser l'objet dans l'inventaire sort cet appareil-là. Si tu veux garder d'autres noms d'objets, change <C>item</C> dans <C>Config.Devices</C>.</Note>
      </>
    ),
  },
  {
    id: "obtenir",
    title: "Obtenir les appareils",
    icon: Gift,
    body: (
      <>
        <P>Tout se règle dans <C>config.lua</C> :</P>
        <Table head={["Réglage", "Valeurs", "Effet"]} rows={[
          [<C>Config.Ownership</C>, <><C>'item'</C> (défaut)</>, "L'appareil est un objet : on peut le perdre, le donner, se le faire voler."],
          ["", <C>'database'</C>, "Ce que le joueur reçoit est enregistré dans la base : impossible à perdre. Utilisé automatiquement sans framework."],
          ["", <C>'free'</C>, "Tout le monde a tout (pour tester)."],
          [<C>Config.StarterPhone</C>, <><C>'auto'</C>, <C>false</C></>, "Yphone offert une seule fois à chaque nouveau personnage. false = il faut l'acheter."],
          [<C>Config.StarterTablet</C>, <><C>true</C> / <C>false</C></>, "Tablette offerte au début."],
          [<><C>job</C> (tablette)</>, <><C>true</C>, <C>false</C>, <C>{'{'} 'police', 'ambulance' {'}'}</C></>, "true = tout joueur qui a un métier de Config.Jobs peut utiliser la tablette sans l'avoir. Une liste = seulement ces métiers."],
        ]} />
        <CodeBlock code={`Config.Devices = {
    ['vantaphone-yphone']    = { label = 'Yphone 18 Pro', kind = 'phone',  item = 'yphone' },
    ['vantaphone-tpadlette'] = { label = 'Tpadlette Pro', kind = 'tablet', item = 'tpadlette', job = true },
}`} />
        <P>Un admin peut donner un appareil avec <C>/vantagive &lt;id&gt; &lt;yphone|tablette&gt;</C>.</P>
        <H3>Un téléphone par joueur</H3>
        <P>Un joueur a un seul téléphone, l'Yphone : son numéro, ses contacts, messages, photos, apps, réglages et fonds d'écran lui appartiennent. Un joueur qui avait l'ancien Sphone reçoit un Yphone avec toutes ses données.</P>
        <Note>Depuis la v1.1.8, <C>Config.Phones</C> (la roue de choix) n'existe plus : si ton <C>config.lua</C> le contient encore, il est ignoré et tu peux retirer le bloc.</Note>
      </>
    ),
  },
  {
    id: "boutique",
    title: "Connecter ta boutique",
    icon: ShoppingBag,
    body: (
      <>
        <P>VantaPhone n'a pas de magasin à lui : tu vends les appareils avec la boutique de ton serveur, au prix que tu veux. Tout se règle dans <C>vantaphone-core/config/shops.lua</C>. Seuls les appareils couverts par ta licence peuvent être donnés aux joueurs.</P>
        <H3>1. ox_inventory (automatique)</H3>
        <P>Le script crée lui-même la boutique ox_inventory :</P>
        <CodeBlock code={`Config.Shop = {
    Mode = 'ox_inventory',
    Prices = { yphone = 1200, tpadlette = 900 },
    OxInventory = {
        Name = 'Vanta Store',
        Blip = { id = 521, colour = 3, scale = 0.8 },
        Locations = {
            vector3(196.9, -933.4, 30.69),   -- ajoute autant d'emplacements que tu veux
        },
        Groups = nil,   -- ou { police = 0 } pour la réserver à un métier
    },
}`} />
        <H3>2. Boutique configurée par fichier</H3>
        <P>qb-shops, esx_shops, jim-shops, ps-shops... : ajoute simplement les objets <C>yphone</C> et <C>tpadlette</C> à la liste des articles de ta boutique, avec ton prix. Laisse <C>Mode = 'none'</C>.</P>
        <H3>3. Ton propre script de boutique</H3>
        <P>Côté serveur, au moment de l'achat, après avoir retiré l'argent :</P>
        <CodeBlock code={`local ok = exports['vantaphone-core']:GiveDevice(source, 'yphone') -- ou 'tablette'
if not ok then
    -- appareil non couvert par la licence ou inventaire plein : rembourse le joueur
end

-- Sans export :
TriggerEvent('vantaphone:server:giveDevice', source, 'yphone', function(ok) end)

-- Le joueur l'a-t-il déjà ?
local has = exports['vantaphone-core']:HasDevice(source, 'yphone')

-- Liste des appareils (nom, objet, image, prix, disponible avec ta licence)
local devices = exports['vantaphone-core']:GetDevices()`} />
        <P>Images à afficher dans ta boutique : <C>nui://vantaphone-core/html/img/yphone.png</C> et <C>tpadlette.png</C>.</P>
      </>
    ),
  },
  {
    id: "metiers",
    title: "Métiers et tablette",
    icon: Briefcase,
    body: (
      <>
        <P>Le téléphone est personnel (appels, SMS, WhatsUp, réseaux sociaux). La tablette est l'outil de travail : le joueur se connecte à l'<b>Espace pro</b> de son métier, prend son service, discute avec ses collègues et, pour la police et les secours, utilise le Terminal (MDT). La tablette ne reçoit ni appels ni SMS.</P>
        <P>Seuls les métiers listés dans <C>Config.Jobs</C> ont un Espace pro. La clé est le nom du métier dans ton framework :</P>
        <CodeBlock code={`Config.Jobs = {
    police    = { label = 'Police',    short = 'LSPD', theme = 'police',   mdt = true, wanted = true },
    ambulance = { label = 'Secours',   short = 'EMS',  theme = 'ems',      mdt = true },
    mechanic  = { label = 'Mécanique', short = 'MECA', theme = 'mechanic' },
    -- ajoute tes métiers :
    vigneron  = { label = 'Domaine',   short = 'VIGN', theme = 'pro' },
}`} />
        <Table head={["Champ", "Rôle"]} rows={[
          [<C>label</C>, "Nom de l'organisation affiché partout (tablette, app Services)."],
          [<C>short</C>, "Sigle de 6 caractères max, utilisé dans les matricules (LSPD-0421)."],
          [<C>theme</C>, <>Couleurs : <C>police</C>, <C>ems</C>, <C>fire</C>, <C>mechanic</C>, <C>taxi</C>, <C>news</C> ou <C>pro</C>.</>],
          [<C>mdt</C>, "true = accès au Terminal et au canal « Inter-services » partagé avec les autres services."],
          [<C>wanted</C>, "true = peut créer et retirer des avis de recherche."],
        ]} />
        <UL>
          <li><b>Prise de service</b> : seulement si le joueur a le métier et est connecté à son Espace pro. Sous QBCore elle suit le service du framework (<C>Config.Duty.SyncFramework</C>).</li>
          <li><b>Plusieurs métiers, une seule organisation</b> : donne-leur le même <C>label</C> (ex. <C>ambulance</C> et <C>ems</C> → « Secours »).</li>
          <li><b>Connexion satellite</b> : un admin peut couper le Terminal d'une organisation avec <C>/vantasatellite &lt;métier&gt; &lt;on|off&gt;</C> (ACE <C>command.vantasatellite</C>).</li>
          <li><b>Sans framework</b> : personne n'a de métier. Mets <C>Config.StandaloneJob = 'police'</C> pour tester.</li>
          <li>Pour ajouter des outils spécifiques à un métier, crée une app métier (voir « Créer tes propres apps »).</li>
        </UL>
      </>
    ),
  },
  {
    id: "touches",
    title: "Touches, gestes et notifications",
    icon: Keyboard,
    body: (
      <>
        <UL>
          <li>Téléphone : <b>M</b> (commande <C>/phone</C>). La même touche range le téléphone s'il est déjà en main.</li>
          <li>Écrire ne déclenche rien : la touche tapée dans le chat (T) ou le champ de texte d'un autre script ne sort pas l'appareil, et pendant qu'on écrit dans le téléphone aucune touche du jeu ne réagit.</li>
          <li>Tablette : <b>N</b> (commande <C>/tablet</C>). La même touche la range ; la touche de l'autre appareil le range et sort celui-ci.</li>
          <li>Chaque joueur change ses touches dans Échap &gt; Paramètres &gt; Raccourcis clavier &gt; FiveM &gt; « VantaPhone ». Les touches par défaut se règlent dans <C>Config.Keys</C> (<C>'COMMA'</C> pour la virgule).</li>
          <li>Seul le téléphone « actif » sonne : celui en main, sinon le dernier sorti. Sans téléphone sur soi, rien ne sonne.</li>
        </UL>
        <P>Le téléphone est tenu d'une main comme dans GTA V, à l'oreille pendant un appel ; la tablette est tenue à deux mains. Les objets et positions se règlent dans <C>Config.Animations</C>.</P>
        <H3>Gestes</H3>
        <UL>
          <li>Remonter la barre du bas vite : accueil. La remonter et s'arrêter : multitâche (balayer une carte vers le haut ferme l'app).</li>
          <li>Tirer le haut de l'écran à gauche : notifications ; à droite : centre de contrôle.</li>
          <li>Glisser depuis le bord gauche d'une app : écran précédent. Appui long sur une icône : raccourcis de l'app, modifier l'écran d'accueil, supprimer l'app.</li>
          <li>Bouton « Rechercher » au-dessus du dock (ou glisser vers le bas sur l'accueil) : apps, contacts et réglages.</li>
          <li>Dynamic Island : une notification sort de l'île ; la toucher l'ouvre en grand pour répondre, la glisser vers le haut la range. Appui long sur l'île : commandes de l'activité en cours (appel, musique, minuteur, chrono).</li>
          <li>Langue : choisie dans l'assistant de bienvenue ou dans Réglages &gt; Général &gt; Langue et région (plus de 70 langues).</li>
        </UL>
        <H3>Notifications</H3>
        <P>Chaque joueur règle ses notifications dans Réglages &gt; Notifications : alertes dans la Dynamic Island ou en bannière, écran verrouillé en Nombre, Pile ou Liste, aperçus, durée d'affichage, réponse rapide, regroupement par app, activités en direct et appels entrants (île ou plein écran). Chaque app a sa page (écran verrouillé, centre de notifications, alertes, style persistant, sons, pastilles). Ces choix sont enregistrés côté serveur et suivent le joueur ; rien à configurer pour le serveur.</P>
      </>
    ),
  },
  {
    id: "telephone",
    title: "Les apps du téléphone",
    icon: Smartphone,
    body: (
      <>
        <P>Ce qu'il faut savoir, côté serveur, sur les apps de l'Yphone. Les autres apps (Messages, WhatsUp, Vistagram, TokTik, Twinter, LeBonPlan, Crush, Notes, Horloge, Météo...) marchent sans rien régler.</P>
        <H3>Apps de base et VantaStore</H3>
        <P>Depuis la 1.13.0, un nouveau téléphone n'a que les apps de base (Téléphone, Messages, Appareil photo, Photos, Réglages, Météo, Horloge, Notes, Calculette, Bank, Véhicules, Vanta Map, Vroum, Boussole, Services...). WhatsUp, Discorde, Spotifi, Vistagram, Ghosty, Twinter, TokTik, Crush, LeBonPlan et Dash s'installent depuis le VantaStore (rubrique « Applications »). Une app de base supprimée se réinstalle depuis la rubrique « Apps d'origine ». Les joueurs qui avaient déjà leur téléphone gardent toutes leurs apps.</P>
        <H3>Boussole, Vanta Map et Vroum</H3>
        <UL>
          <li><b>Boussole</b> : cap en direct, cap gardé en touchant le cadran (un arc rouge montre l'écart), niveau à bulle, lieu, coordonnées et altitude.</li>
          <li><b>Vanta Map</b> : l'île avec les rues autour du joueur (lues dans le jeu, sans image de carte), recherche de lieux (hôpitaux, police, stations-service, garages, banques... et les blips de ton serveur), fiche d'un lieu avec son adresse, favoris, Domicile, Travail, récents, repère au doigt, itinéraire tracé par le GPS du jeu avec la prochaine consigne et l'heure d'arrivée.</li>
          <li><b>Vroum</b> : GPS plein écran qui tourne avec la voiture, vitesse et alerte de vitesse, heure d'arrivée, et signalements des joueurs (police, accident, bouchon, danger, véhicule arrêté, route fermée) que les autres conducteurs confirment ou infirment. Un signalement disparaît tout seul après quelques minutes. Le GPS marche sans forfait, les signalements passent par VantaLink.</li>
        </UL>
        <P>Un point posé sur la carte du jeu (menu Pause) est repris par Vanta Map et Vroum. Aucune base de données : les signalements vivent en mémoire.</P>
        <CodeBlock code={`Config.Vroum = {
    Enabled = true,
    PoliceReports = true,      -- false : impossible de signaler la police
    ShowDrivers = true,        -- montrer (anonymement) les autres conducteurs qui ont Vroum ouvert
    Range = 3000,              -- rayon (mètres) des signalements affichés
    MaxReportsPerHour = 12,    -- signalements par joueur et par heure
}`} />
        <H3>Bank</H3>
        <P>L'appli Bank utilise l'argent du serveur : le solde est celui du compte en banque du framework (ESX : compte <C>bank</C>, QBCore : <C>money.bank</C>, ox_core : compte du personnage). Sans framework, ou avec <C>Account = 'internal'</C>, la banque garde son propre solde. Chaque joueur a un RIB (IBAN, BIC) et une carte. Il peut virer par numéro ou par IBAN (le nom du bénéficiaire s'affiche avant l'envoi), demander de l'argent, payer ses factures et voir son historique (salaires, retraits et achats du framework compris). Un joueur hors ligne reçoit ses virements à sa prochaine connexion. Les tables <C>vantaphone_bank_*</C> sont créées toutes seules.</P>
        <P>Factures : celles de l'appli, d'<C>esx_billing</C> et des <C>phone_invoices</C> de qb-phone s'affichent et se paient dans l'appli. Les métiers de <C>InvoiceJobs</C> en service facturent leurs clients ; l'argent va au compte de l'entreprise (esx_addonaccount, qb-banking, qb-management ou Renewed-Banking), sinon à l'employé.</P>
        <CodeBlock code={`Config.Bank = {
    Enabled = true,
    Name = 'Vanta Bank',          -- nom de la banque (RIB, carte)
    Bic = 'VNTAFRPPXXX',
    IbanCountry = 'FR',
    Currency = 'USD',             -- devise affichée
    Account = 'framework',        -- 'framework' ou 'internal'
    StartBalance = 2500,          -- solde de départ en mode interne
    MaxTransfer = 1000000,
    DailyLimit = 0,               -- plafond de virements par jour (0 = aucun)
    MaxInvoice = 100000,
    InvoiceJobs = { police = true, ambulance = true, mechanic = true, taxi = true },
    HistoryDays = 60,
    TrackFrameworkHistory = true, -- salaires, retraits, achats dans l'historique
    EsxBilling = true,
    QBPhoneInvoices = true,
}`} />
        <P>Depuis un autre script (côté serveur) :</P>
        <CodeBlock code={`-- facture payée au compte de l'entreprise
exports['vantaphone-core']:CreateInvoice(source, 250, 'Réparation moteur', { society = 'mechanic' })
-- ligne dans l'historique (achat, loyer...)
exports['vantaphone-core']:AddBankTransaction(source, -45, 'Achat 24/7', 'card')
local iban = exports['vantaphone-core']:GetBankIban(source)`} />
        <H3>Services : appeler et écrire</H3>
        <P>Sur l'Yphone, l'appli Services permet d'appeler ou d'écrire à chaque organisation de <C>Config.Jobs</C>. Un appel sonne chez tous les membres en service : le premier qui décroche prend l'appel. Un message (texte, photo, position) arrive chez tous les membres en service, qui répondent depuis la carte « Messages du service ». Les conversations sont gardées 7 jours (table <C>vantaphone_service_messages</C>, créée toute seule).</P>
        <P>Voix des appels : pma-voice est utilisé tout seul s'il tourne. Avec un autre système vocal, mets <C>Voice = false</C> et écoute les événements serveur.</P>
        <CodeBlock code={`Config.Calls = { Voice = true }

-- autre système vocal (côté serveur)
AddEventHandler('vantaphone:server:callStarted', function(callId, src1, src2) end)
AddEventHandler('vantaphone:server:callEnded', function(callId, src1, src2) end)`} />
        <H3>Position en direct et photos</H3>
        <P>Dans Messages et WhatsUp, le bouton + envoie une photo ou la position en direct pendant 15 min à 10 h. Le destinataire voit une carte qui se met à jour (rue, quartier, distance, itinéraire GPS). Les photos dans les SMS sont gérées (colonne ajoutée toute seule). Dans Photos, le + importe une image depuis le PC (Ctrl+V, fichier ou lien https) et « Vers le PC » copie ou télécharge une photo. Le choix de fichier et le téléchargement dépendent du navigateur de FiveM ; le copier-coller marche partout.</P>
        <H3>Véhicules</H3>
        <P>L'appli Véhicules (Yphone) lit les véhicules du joueur dans la table du garage du serveur : ESX (<C>owned_vehicles</C>) et QBCore / Qbox (<C>player_vehicles</C>) sont reconnus tout seuls. Un véhicule sorti est localisé (OneSync) avec itinéraire GPS. « Signaler à la police » envoie aux métiers de <C>ReportJobs</C> en service une alerte et un point qui suit le véhicule en direct pendant <C>ReportMinutes</C>, et crée un appel dans le Terminal.</P>
        <CodeBlock code={`Config.Vehicles = {
    Enabled = true,
    Garage = 'auto',            -- 'auto' | 'esx' | 'qb' | 'custom'
    -- garage personnalisé :
    Table = 'my_garage',        -- table
    OwnerColumn = 'owner',      -- identifiant du joueur
    PlateColumn = 'plate',
    PropsColumn = 'vehicle',    -- JSON des propriétés (modèle), ou nil
    ModelColumn = nil,          -- ou colonne du modèle
    StoredColumn = 'stored',    -- au garage ?
    StoredValue = 1,
    ImpoundValue = 2,           -- fourrière, ou nil
    GarageColumn = 'parking',   -- nom du garage, ou nil
    ReportJobs = { 'police', 'sheriff' },
    ReportMinutes = 5,
}`} />
        <P>Pour un autre script (MDT, dispatch) :</P>
        <CodeBlock code={`AddEventHandler('vantaphone:server:vehicleReported', function(src, plate, coords, minutes) end)
local reported = exports['vantaphone-core']:GetReportedVehicles()`} />
        <H3>Ghosty</H3>
        <P>Les amis Ghosty sont les contacts qui ont aussi le joueur dans leurs contacts. La carte montre la position en direct des amis connectés (quartier, rue, distance, itinéraire GPS) sauf ceux en mode fantôme ; elle demande OneSync. Les snaps envoyés à des amis disparaissent une fois vus ; Spotlight montre les publications publiques.</P>
        <CodeBlock code={`Config.Ghosty = {
    Map = true,                 -- carte des amis (OneSync)
    GhostModeByDefault = false, -- true : position cachée tant que le joueur ne la partage pas
    SnapHours = 24,             -- un snap non ouvert disparaît après ce délai
    SpotlightDays = 7,          -- durée d'affichage d'une publication Spotlight
    MaxPhotoSize = 1500000,
}`} />
        <H3>Appareil photo</H3>
        <P>L'écran de l'appareil montre la vraie vue du jeu (caméra arrière, ou avant pour un selfie) :</P>
        <UL>
          <li>Glisser dans le viseur pour viser, clic pour la mise au point.</li>
          <li>Zoom : pastilles 0,5× à 8×, glisser sur les pastilles (molette), molette de la souris ou flèches, jusqu'à 40×. Selfie : 1× à 3×.</li>
          <li>Flash Auto / Oui / Non : vraie lumière dans le jeu, vue par les joueurs autour. Retardateur 3 s / 10 s, format carré.</li>
          <li>Déclencheur : bouton rond, Espace, Entrée ou boutons de volume.</li>
        </UL>
        <P>Pour que la photo soit une capture du jeu, installe <C>screenshot-basic</C> (voir <C>Config.Camera</C>). Un autre script peut savoir si l'appareil photo est ouvert avec <C>exports['vantaphone-core']:IsCameraActive()</C>.</P>
        <P>Bloquer les appareils depuis un autre script (menotté, KO...), côté client :</P>
        <CodeBlock code={`exports['vantaphone-core']:SetDisabled(true)   -- range l'appareil et empêche de le sortir
exports['vantaphone-core']:SetDisabled(false)`} />
        <H3>Navigateur Web</H3>
        <P>L'appli Web cherche avec Google ; beaucoup de sites refusent de s'afficher dans une autre page, le bouton « Sur le PC » les ouvre dans le navigateur du PC.</P>
        <CodeBlock code={`-- sites interdits dans l'appli Web (et leurs sous-domaines)
Config.Browser.Blocked = { 'exemple.com' }`} />
        <H3>Accueil, widgets et Dynamic Island</H3>
        <P>Appui long sur un widget (ou « Modifier l'écran d'accueil ») : le + ajoute un widget (Météo, Horloge, Heure, Aujourd'hui, Calendrier, Batterie, Photos), le − le retire, et « Personnaliser » change le style des icônes (par défaut, sombre, teinté, transparent) et leur taille. Huit nouveaux fonds d'écran s'assombrissent en mode sombre. En silencieux, en sonnerie, en Ne pas déranger, en mode Avion ou quand la lampe s'allume avec le bouton Action, la Dynamic Island s'étire sur tout le haut de l'écran.</P>
        <H3>Bouton Action</H3>
        <P>Le bouton sur le côté gauche de l'Yphone est un bouton Action : maintenir déclenche l'action choisie dans Réglages &gt; Bouton Action (mode silencieux, concentration, appareil photo, lampe torche, ouvrir une app ou aucune action). Un appui court affiche l'action dans la Dynamic Island.</P>
        <H3>Dash</H3>
        <P>Jeu de rythme à télécharger dans le VantaStore : 5 niveaux originaux, vaisseau, orbes, gravité, portails de vitesse, mode entraînement et personnalisation du cube. Le téléphone passe en paysage pour jouer. Espace, flèche haut, clic ou toucher pour sauter ; Échap ou P pour la pause. Rien à configurer.</P>
        <H3>Fonctions retirées</H3>
        <P>Les apps d'IA Nova et Astra et le jeu Bastion ont été retirés en 1.10.0 : les convars <C>vantaphone_spotify_id</C> et <C>vantaphone_spotify_secret</C> ne servent plus. L'ancienne app Relais (Discord) est remplacée par Discorde en 1.13.0 (voir « Discorde ») : un <C>Config.Discord</C> de Relais est repris tel quel.</P>
        <P>L'assistant vocal a été retiré en 1.12.0 : les convars <C>vantaphone_gemini_key</C>, <C>vantaphone_openai_key</C> et <C>vantaphone_*_model</C> ne servent plus, et <C>Config.AI</C>, <C>Config.Assistant</C> et <C>Config.Keys.Assistant</C> peuvent être supprimés de <C>config.lua</C>. Le bouton latéral range simplement le téléphone.</P>
      </>
    ),
  },
  {
    id: "musique",
    title: "Spotifi (musique)",
    icon: Music,
    body: (
      <>
        <P>Spotifi est une vraie appli de streaming : Accueil (titres, albums, artistes et playlists du moment, classement), Rechercher par catégories, Bibliothèque (titres likés, playlists perso, artistes et podcasts suivis), onglet FiveM, mini-lecteur, lecteur plein écran, file d'attente, Dynamic Island et centre de contrôle.</P>
        <Table head={["Fonction", "Ce qu'il faut"]} rows={[
          ["Vrais titres, pochettes, classements, recherche", <b>Rien</b>],
          ["Titres écoutés en entier (vidéo YouTube du titre)", <><b>Rien</b>. Clé YouTube facultative.</>],
          ["Podcasts (épisodes complets) et radios en direct", <b>Rien</b>],
          ["Diffuser la musique aux joueurs autour", <><b>Rien</b> (portée : <C>Config.Tunes</C>).</>],
          ["Relier son vrai compte Spotify", <>Une appli gratuite sur developer.spotify.com, et <b>Spotify Premium</b> pour chaque joueur.</>],
          ["Musique FiveM (musique des joueurs, validée par les fondateurs)", <>La permission <C>vantaphone.music</C> pour ceux qui valident.</>],
        ]} />
        <H3>Titres en entier</H3>
        <P>Les titres et leurs pochettes viennent d'un catalogue musical public. Quand un joueur lance un titre, le téléphone le joue <b>en entier avec sa vidéo YouTube</b>. Dans Spotifi &gt; roue dentée, chaque joueur peut couper « Titres complets » (extraits de 30 s) ou activer « Afficher le clip » (la vidéo remplace la pochette). Si aucune vidéo lisible n'est trouvée, l'extrait de 30 s est joué et le lecteur l'indique. Les vidéos trouvées sont gardées en base : chaque titre n'est cherché qu'une fois pour tout le serveur. YouTube peut afficher une publicité avant certains titres, et quelques vidéos refusent d'être lues ailleurs que sur YouTube (l'extrait les remplace).</P>
        <P>Sans clé, la recherche des vidéos est limitée à environ 10 nouveaux titres par minute pour tout le serveur (les titres déjà trouvés ne comptent pas). Pour un serveur très peuplé, ajoute une clé YouTube gratuite (console.cloud.google.com &gt; activer « YouTube Data API v3 » &gt; Identifiants &gt; Créer une clé API) et, si tu en as une, une clé Odesli :</P>
        <CodeBlock lang="cfg" code={`# server.cfg (facultatif)
set vantaphone_youtube_key "..."
set vantaphone_odesli_key "..."`} />
        <P>« Diffuser » dans le lecteur fait entendre le titre en cours aux joueurs autour (portée <C>Config.Tunes.MaxRange</C>, 10 m par défaut). Sinon, chaque joueur est seul à entendre sa musique.</P>
        <H3>Relier son vrai compte Spotify</H3>
        <P>Dans Spotifi &gt; Bibliothèque &gt; « Relier mon Spotify », le joueur se connecte à Spotify sur la page qui s'ouvre sur son PC. Il retrouve ensuite dans le téléphone ses playlists, ses titres likés, ses écoutes récentes et la recherche Spotify ; « Écouter sur mon Spotify » est aussi dans le menu « ⋯ » d'un titre du catalogue. La musique joue <b>en entier dans son appli Spotify</b> (PC ou téléphone réel) et le téléphone du jeu la commande : lecture, pause, suivant, précédent, position, aléatoire, répétition, like.</P>
        <Note type="warn">Conditions posées par Spotify : un compte <b>Premium</b> pour lancer la musique (un compte gratuit peut seulement parcourir), et l'appli Spotify ouverte sur le PC du joueur.</Note>
        <P>Pour l'activer sur ton serveur (gratuit) :</P>
        <Steps items={[
          <>Trouve l'adresse de ton serveur : tape <C>web_baseUrl</C> dans la console du serveur (ex. <C>tonnom-abc123.users.cfx.re</C>). Ta Redirect URI est <C>https://</C> + cette adresse + <C>/vantaphone-core/spotify/callback</C>.</>,
          <>Sur developer.spotify.com, connecte-toi puis Dashboard &gt; <b>Create app</b>. Donne un nom et une description, colle ta Redirect URI dans <b>Redirect URIs</b>, coche <b>Web API</b>, puis enregistre.</>,
          <>Dans les réglages de l'appli, copie le <b>Client ID</b> (le Client secret ne sert pas), ajoute-le à ton <C>server.cfg</C> et redémarre le serveur. La console affiche alors en vert la Redirect URI exacte attendue : elle doit être identique à celle de l'appli Spotify.</>,
          <>Tant que l'appli Spotify est en <b>Development mode</b>, seuls les comptes ajoutés dans <b>User Management</b> (nom et email Spotify du joueur) peuvent se relier, 25 au maximum.</>,
        ]} />
        <CodeBlock lang="cfg" code={`# server.cfg
set vantaphone_spotify_client_id "..."
# seulement si la console dit que l'adresse publique est inconnue :
set vantaphone_spotify_redirect "https://.../vantaphone-core/spotify/callback"`} />
        <P>Le serveur ne voit jamais le mot de passe Spotify : il garde seulement le jeton de connexion (table <C>vantaphone_spotify_accounts</C>). Le joueur peut se déconnecter à tout moment (Mon Spotify &gt; Déconnecter Spotify).</P>
        <H3>Musique FiveM</H3>
        <P>Dans l'onglet FiveM de Spotifi, les joueurs proposent leur propre musique : fichier mp3, ogg, m4a ou wav depuis le PC, ou lien https direct vers le fichier, avec titre, nom d'artiste, genre et pochette. Les fondateurs reçoivent une notification, écoutent la demande puis <b>l'acceptent</b> (le titre est publié) ou <b>la refusent</b> (avec une raison envoyée au joueur). La musique publiée n'existe <b>que sur ton serveur</b> : nouveautés, les plus écoutées, recherche et bibliothèque.</P>
        <P>Valident les demandes : ceux qui ont la permission <C>vantaphone.music</C>, les admins qui ont déjà <C>command.vantaupdate</C>, et les identifiants listés dans <C>Moderators.Identifiers</C> (ex. <C>license:...</C>, <C>discord:...</C>).</P>
        <CodeBlock lang="cfg" code={`# server.cfg
add_ace group.admin vantaphone.music allow`} />
        <UL>
          <li>Les fichiers sont rangés dans <C>vantaphone-core/music/</C> : la ligne <C>add_filesystem_permission vantaphone-core write vantaphone-core</C> de l'installation est nécessaire. <C>vantaupdate</C> ne touche pas à ce dossier ; garde-le si tu mets à jour à la main.</li>
          <li>Les joueurs écoutent les fichiers par l'adresse web du serveur (<C>web_baseUrl</C>, automatique sur un serveur listé). Si elle est vide, remplis <C>PublicUrl</C> ; sans adresse, les fichiers passent par le jeu, plus lentement.</li>
          <li>Rappelle aux joueurs de n'envoyer que leur propre musique, ou une musique dont ils ont les droits.</li>
        </UL>
        <CodeBlock code={`-- config.lua (facultatif : sans ce bloc, ces valeurs sont utilisées)
Config.Music = {
    Enabled = true,
    Country = 'FR',              -- pays du catalogue et des classements
    FiveM = {
        Enabled = true,          -- onglet FiveM et publication
        MaxFileMB = 10,          -- taille max d'un fichier envoyé
        MaxPending = 3,          -- demandes en attente par joueur
        AllowLinks = true,       -- accepter les liens https directs
        Moderators = { Ace = 'vantaphone.music', Identifiers = {} },
        PublicUrl = '',          -- adresse https du serveur si web_baseUrl est vide
    },
}`} />
        <Note>Depuis 1.11.0, <C>Config.Music</C> remplace <C>Config.Spotify</C> (tu peux retirer l'ancien bloc). Les tables <C>vantaphone_music_*</C> et <C>vantaphone_spotify_accounts</C> sont créées toutes seules.</Note>
      </>
    ),
  },
  {
    id: "apps",
    title: "Créer tes propres apps",
    icon: Code2,
    body: (
      <>
        <P>N'importe quelle ressource peut ajouter une app au téléphone et/ou à la tablette, par exemple les outils d'un métier. L'app est une page web (HTML/CSS/JS) affichée dans l'appareil. Un exemple complet est fourni : <C>vantaphone-exemple-app</C> (une app « Mon Garage » sur le téléphone et une app « Contrôle » réservée à la police sur la tablette).</P>
        <H3>1. Déclarer l'app (client.lua de ta ressource)</H3>
        <CodeBlock code={`exports['vantaphone-core']:AddApp({
    id = 'garage',                        -- unique : lettres, chiffres, _ et -
    label = 'Mon Garage',
    description = 'Tes véhicules',         -- affiché dans VantaStore
    device = 'phone',                      -- 'phone', 'tablet' ou 'all'
    ui = 'https://cfx-nui-mon_garage/html/index.html',
    icon = 'https://cfx-nui-mon_garage/html/icon.png', -- facultatif
    color = '#1e90ff',                     -- fond de l'icône sans image
    jobs = { 'mechanic' },                 -- facultatif : seulement ces métiers
    requirePro = false,                    -- tablette : connecté à l'Espace pro
    needsData = false,                     -- téléphone : forfait data nécessaire
})`} />
        <Note type="warn">Le core oublie les apps quand il redémarre : ré-ajoute-les sur l'événement <C>onClientResourceStart</C> de <C>vantaphone-core</C> (voir l'exemple). Les fichiers de ta page doivent être listés dans <C>files</C> de ton fxmanifest.</Note>
        <H3>2. La page</H3>
        <CodeBlock lang="html" code={`<script src="https://cfx-nui-vantaphone-core/sdk/vantaphone.js"></script>
<script>
  VantaPhone.onOpen((ctx) => {
    // ctx.device ('phone' | 'tablet'), ctx.theme ('light' | 'dark'), ctx.accent,
    // ctx.job, ctx.jobLabel, ctx.onDuty, ctx.name, ctx.number
  });

  const vehicles = await VantaPhone.request('list');     // réponse de ton Lua
  VantaPhone.send('locate', { plate: 'VANTA 01' });      // événement simple
  VantaPhone.onMessage((data) => { /* envoyé par ton Lua */ });
  VantaPhone.notify('Garage', 'Ta voiture est prête');
  VantaPhone.close();                                    // retour à l'accueil
</script>`} />
        <H3>3. Répondre depuis ton Lua (client)</H3>
        <CodeBlock code={`exports['vantaphone-core']:RegisterAppCallback('garage', 'list', function(data)
    return { { plate = 'VANTA 01', model = 'Sultan RS' } }
end)

AddEventHandler('vantaphone:client:appEvent', function(appId, event, data)
    if appId == 'garage' and event == 'locate' then --[[ ... ]] end
end)

exports['vantaphone-core']:SendAppMessage('garage', { refresh = true })
exports['vantaphone-core']:NotifyApp('garage', { title = 'Garage', text = 'Ta voiture est prête' })`} />
        <P>Les champs de texte de ta page bloquent automatiquement les déplacements du personnage pendant la saisie, et la page suit le thème clair / sombre de l'appareil.</P>
      </>
    ),
  },
  {
    id: "reglages",
    title: "Autres réglages",
    icon: SlidersHorizontal,
    body: (
      <>
        <Table head={["Réglage", "À quoi ça sert"]} rows={[
          [<C>Config.Framework</C>, <><C>'auto'</C> détecte ESX, QBCore, ox_core ou standalone. Tu peux le forcer.</>],
          [<C>Config.PhoneNumber</C>, "Longueur et format des numéros de téléphone."],
          [<C>Config.Carrier</C>, "VantaLink : forfaits data (prix, durée) nécessaires aux apps en ligne (WhatsUp, Vistagram...)."],
          [<C>Config.Battery</C>, <>Batterie illimitée par défaut : téléphone et tablette toujours à 100 %. <C>Unlimited = false</C> : batterie réaliste (une par appareil, recharge en voiture).</>],
          [<C>Config.Tunes</C>, "Spotifi : portée de la musique diffusée aux joueurs autour."],
          [<C>Config.Music</C>, "Spotifi : pays du catalogue et Musique FiveM (taille des fichiers, demandes en attente, liens, validation)."],
          [<C>Config.Hotspot</C>, "Portée du partage de connexion."],
          [<C>Config.Satellite</C>, "Connexion satellite de la tablette activée par défaut ou non."],
          [<C>Config.Services</C>, "Nom et icône de l'app Services (qui est en service)."],
          [<C>Config.WhatsUp</C>, "Taille max des groupes et des photos."],
          [<C>Config.Camera</C>, <>Ressource de capture d'écran pour de vraies photos (ex. <C>screenshot-basic</C>).</>],
          [<C>Config.Debug</C>, "Affiche plus d'informations dans la console."],
        ]} />
        <H3>Clés du server.cfg</H3>
        <P>Les clés secrètes vont dans <C>server.cfg</C>, jamais dans <C>config.lua</C> (envoyé aux joueurs). Toutes sont facultatives : sans elles, la fonction concernée est simplement désactivée ou limitée.</P>
        <Table head={["Convar", "Pour quoi"]} rows={[
          [<C>vantaphone_youtube_key</C>, "Spotifi : recherche des titres en entier plus sûre quand beaucoup de joueurs écoutent."],
          [<C>vantaphone_odesli_key</C>, "Spotifi : enlève la limite d'environ 10 recherches par minute."],
          [<C>vantaphone_spotify_client_id</C>, "Spotifi : relier son vrai compte Spotify."],
          [<C>vantaphone_spotify_redirect</C>, "Spotifi : adresse de retour de Spotify, seulement si l'adresse publique du serveur est inconnue."],
        ]} />
      </>
    ),
  },
  {
    id: "commandes",
    title: "Commandes et exports",
    icon: Terminal,
    body: (
      <>
        <Table head={["Commande", "Qui", "Effet"]} rows={[
          [<C>vantalicense</C>, "Console", "Revérifie la licence et affiche le résultat."],
          [<C>vantaupdate</C>, <>Console ou ACE <C>command.vantaupdate</C></>, <>Installe la dernière version, ou les fichiers qui manquent si elle est déjà en place. <C>check</C> : vérifier, <C>force</C> : tout retélécharger pour réparer, <C>test</C> : vérifier que les fichiers peuvent être écrits, <C>retour</C> : revenir à la version précédente, <C>04:00</C> : installer à 4 h, <C>annuler</C>, <C>statut</C>, <C>notes</C>, <C>aide</C>.</>],
          [<C>/vantagive &lt;id&gt; &lt;appareil&gt;</C>, <>ACE <C>command.vantagive</C></>, "Donne un Yphone (yphone) ou une tablette (tablette)."],
          [<C>/vantabattery &lt;id&gt; &lt;0-100&gt; [appareil]</C>, <>ACE <C>command.vantabattery</C></>, <>Règle la batterie d'un joueur (seulement avec <C>Unlimited = false</C>).</>],
          [<C>/vantasatellite &lt;métier&gt; &lt;on|off&gt;</C>, <>ACE <C>command.vantasatellite</C></>, "Coupe ou rétablit le Terminal d'une organisation."],
        ]} />
        <CodeBlock lang="cfg" code={`# server.cfg : autoriser le groupe admin
add_ace group.admin command.vantagive allow
add_ace group.admin command.vantabattery allow
add_ace group.admin command.vantasatellite allow
add_ace group.admin command.vantaupdate allow
add_ace group.admin vantaphone.music allow   # valider la Musique FiveM

# vantaupdate : redémarrer tout seul les téléphones après une mise à jour
add_ace resource.vantaphone-core command.refresh allow
add_ace resource.vantaphone-core command.restart allow
add_ace resource.vantaphone-core command.stop allow
add_ace resource.vantaphone-core command.start allow`} />
        <H3>Exports serveur</H3>
        <UL>
          <li><C>GiveDevice(source, appareil)</C>, <C>HasDevice(source, appareil)</C>, <C>GetDevices()</C> : boutique.</li>
          <li><C>GetBattery(identifier, [appareil])</C>, <C>SetBattery(identifier, niveau, [appareil])</C>, <C>AddBattery(identifier, quantité, [appareil])</C> : chargeurs, batteries externes (avec <C>Unlimited = false</C>).</li>
          <li><C>IsDeviceLicensed('vantaphone-yphone')</C>.</li>
        </UL>
        <H3>Exports client</H3>
        <UL>
          <li><C>OpenPhone()</C>, <C>OpenTablet()</C>, <C>SetDisabled(bool)</C>, <C>GetActivePhone()</C>.</li>
          <li><C>AddApp</C>, <C>RemoveApp</C>, <C>RegisterAppCallback</C>, <C>SendAppMessage</C>, <C>NotifyApp</C> : apps perso.</li>
          <li>Événement <C>vantaphone:client:callMuted</C> (true / false) : à brancher sur ton système vocal (pma-voice, saltychat...).</li>
        </UL>
      </>
    ),
  },
  {
    id: "depannage",
    title: "Dépannage",
    icon: LifeBuoy,
    body: (
      <>
        <Table head={["Problème", "Solution"]} rows={[
          ["Le téléphone ne s'ouvre pas", "Regarde la console du serveur : licence, dossier mal nommé ou objet manquant y sont signalés. Le joueur voit aussi un message au-dessus de la mini-carte."],
          ["« Tu n'as pas de téléphone sur toi »", <>Le joueur n'a pas l'objet. Donne-le avec <C>/vantagive</C> ou vérifie <C>Config.StarterPhone</C>.</>],
          ["Pas d'image dans l'inventaire", <>Copie les images de <C>install/images/</C> dans le dossier d'images de ton inventaire, avec le même nom que l'objet.</>],
          ["La tablette refuse le Terminal", <>Le métier doit avoir <C>mdt = true</C> dans <C>Config.Jobs</C> et le joueur doit être connecté à son Espace pro.</>],
          ["Les apps en ligne disent « pas de connexion »", "Le joueur doit acheter un forfait dans l'app VantaLink (ou quelqu'un doit lui partager sa connexion)."],
          ["Les photos sont des images générées", <>Installe <C>screenshot-basic</C> (voir <C>Config.Camera</C>).</>],
          ["Changement d'hébergeur", "« Changer d'appareil » sur la carte du produit, puis redémarre le nouveau serveur."],
        ]} />
        <H3>Spotifi et Spotify</H3>
        <Table head={["Problème", "Solution"]} rows={[
          ["Les titres jouent seulement 30 s", <>Vérifie que « Titres complets » est activé (Spotifi &gt; roue dentée). Si le lecteur dit « Beaucoup de demandes », ajoute <C>vantaphone_youtube_key</C>. Certaines vidéos refusent d'être lues ailleurs que sur YouTube : l'extrait les remplace.</>],
          ["La carte « Relier mon Spotify » n'apparaît pas", <>Ajoute <C>vantaphone_spotify_client_id</C> dans <C>server.cfg</C> et redémarre le serveur. Il faut aussi que l'adresse publique du serveur soit connue (voir la ligne suivante).</>],
          ["Spotify affiche « INVALID_CLIENT: Invalid redirect URI »", "L'adresse dans Redirect URIs doit être exactement celle affichée dans la console (en https, sans espace ni / en trop)."],
          ["La console dit « adresse publique inconnue »", <>Ton serveur n'est pas listé : règle <C>vantaphone_spotify_redirect</C> avec son adresse https publique, et mets la même dans Redirect URIs.</>],
          ["« Spotify a refusé la connexion » ou « a refusé cette action »", "Ajoute le compte Spotify du joueur dans User Management (Development mode, 25 comptes max)."],
          ["« Spotify Premium est nécessaire »", "Le joueur a un compte gratuit : Spotify ne permet de lancer la musique qu'avec Premium."],
          ["« Ouvre l'appli Spotify sur ton PC »", "Aucune appli Spotify n'est ouverte : le joueur ouvre Spotify sur son PC (ou son téléphone réel) puis réessaie."],
          ["Musique FiveM : « le serveur ne peut pas enregistrer de fichier »", <>Ajoute <C>add_filesystem_permission vantaphone-core write vantaphone-core</C> dans <C>server.cfg</C> et redémarre le serveur, ou autorise seulement les liens.</>],
          ["Musique FiveM : les fondateurs ne reçoivent pas les demandes", <>Donne-leur la permission <C>vantaphone.music</C> (ou ajoute leur identifiant dans <C>Moderators.Identifiers</C>).</>],
        ]} />
        <P>Toujours bloqué ? Écris-nous depuis la page <Link to="/support" className="text-blue-400 hover:underline">Support</Link> avec le message de la console.</P>
      </>
    ),
  },
  {
    id: "maj",
    title: "Mettre à jour",
    icon: RefreshCw,
    body: (
      <>
        <P>Ton serveur cherche une nouvelle version toutes les 5 minutes, sans redémarrer : dès qu'elle sort, la console l'annonce avec ses nouveautés, et les admins en jeu reçoivent un message.</P>
        <Steps items={[
          <>Tape <C>vantaupdate</C> dans la console du serveur (ou <C>/vantaupdate</C> en jeu avec la permission <C>command.vantaupdate</C>). Il redemande toujours la dernière version au site : une version sortie il y a une minute s'installe tout de suite.</>,
          "Seuls les fichiers modifiés sont téléchargés, et chacun est contrôlé (taille et empreinte SHA-256) avant d'être écrit : un fichier abîmé est redemandé. Ton dossier config n'est jamais remplacé : tes réglages et ta clé restent, comme les musiques envoyées par les joueurs.",
          "Les fichiers remplacés sont sauvegardés, et ceux qu'une version a retirés sont supprimés.",
          "Les téléphones redémarrent après un préavis d'une minute affiché aux joueurs (rappel 10 s avant).",
          "Les joueurs reçoivent ensuite la « Mise à jour logicielle » sur l'appareil qui a changé : notification, puis Installer maintenant ou Plus tard, avec le téléchargement et le redémarrage comme sur un vrai téléphone.",
        ]} />
        <CodeBlock code={`Config.Updates = {
    Auto = false,        -- true : installe toute seule chaque nouvelle version
    AutoTime = '',       -- '04:00' : l'installation automatique attend 4 h
    RestartDelay = 60,   -- préavis (secondes) avant de redémarrer les téléphones
    NotifyAdmins = true, -- prévenir les admins en jeu
    CheckMinutes = 5,    -- chercher une nouvelle version toutes les 5 min (0 = désactivé)
    Backup = true,       -- garder les fichiers remplacés (vantaupdate retour)
}`} />
        <H3>Revenir en arrière, programmer (1.13+)</H3>
        <UL>
          <li><C>vantaupdate retour</C> remet la version précédente depuis la sauvegarde ; cette version n'est plus proposée en automatique jusqu'à la suivante.</li>
          <li><C>vantaupdate 04:00</C> programme l'installation à 4 h (les joueurs sont prévenus), <C>vantaupdate annuler</C> l'annule.</li>
          <li><C>vantaupdate statut</C> : version installée, dernière installation, sauvegarde et installation prévue. <C>vantaupdate notes</C> : nouveautés de la dernière version. <C>vantaupdate aide</C> : toutes les commandes.</li>
        </UL>
        <H3>« N fichier(s) n'ont pas pu être écrits »</H3>
        <P>Les serveurs FiveM récents ont un sandbox : sans autorisation, une ressource ne peut pas écrire de scripts (<C>.lua</C>), donc <C>vantaupdate</C> ne peut rien installer. Ajoute ces lignes dans <C>server.cfg</C>, avant les <C>ensure</C> de VantaPhone, puis redémarre le serveur une fois :</P>
        <CodeBlock lang="cfg" code={`add_filesystem_permission vantaphone-core write vantaphone-core
add_filesystem_permission vantaphone-core write vantaphone-yphone
add_filesystem_permission vantaphone-core write vantaphone-tpadlette
add_filesystem_permission vantaphone-core write vantaphone-exemple-app`} />
        <P>Tape ensuite <C>vantaupdate test</C> (depuis la 1.1.12) : la console confirme que les mises à jour peuvent s'installer. Au démarrage, VantaPhone prévient tout seul s'il ne peut pas écrire ses fichiers, avec les lignes à ajouter. Si Windows bloque le dossier, la console le dit aussi : dossier en lecture seule, « Accès contrôlé aux dossiers », OneDrive ou antivirus.</P>
        <P>Une mise à jour n'a écrit qu'une partie des fichiers (par exemple les téléphones d'une ancienne version) ? Une fois les lignes ajoutées et le serveur redémarré, retape <C>vantaupdate</C> : depuis la 1.1.13, il installe les fichiers qui manquent, même si la version affichée est déjà la dernière.</P>
        <H3>« Access denied for command stop » (ou start)</H3>
        <P>FiveM fait un <C>restart</C> avec les commandes <C>stop</C> puis <C>start</C> : pour que <C>vantaupdate</C> redémarre lui-même VantaPhone, <C>vantaphone-core</C> a besoin de leur droit aussi. Ajoute ces lignes dans <C>server.cfg</C> et redémarre le serveur :</P>
        <CodeBlock lang="cfg" code={`add_ace resource.vantaphone-core command.refresh allow
add_ace resource.vantaphone-core command.restart allow
add_ace resource.vantaphone-core command.stop allow
add_ace resource.vantaphone-core command.start allow`} />
        <P>En attendant, les fichiers sont bien installés : tape <C>restart vantaphone-core</C> dans la console (ou redémarre le serveur) pour activer la mise à jour. Depuis la 1.1.14, la console donne ces lignes elle-même quand il en manque.</P>
        <H3>À la main</H3>
        <Steps items={[
          "Télécharge le nouveau zip depuis ton tableau de bord.",
          <>Remplace les dossiers de VantaPhone en gardant ton dossier <C>config</C> et le dossier <C>vantaphone-core/music</C> (musiques des joueurs).</>,
          <>Réimporte <C>vantaphone.sql</C> : il ajoute les nouvelles tables sans toucher aux données.</>,
          "Redémarre le serveur. Une ancienne config reste compatible : les nouveaux réglages prennent leur valeur par défaut.",
        ]} />
      </>
    ),
  },
];

export default function VantaPhoneDocs() {
  const { user } = useAuth();
  const [bots, setBots] = useState(null);
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/bots`, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
        setBots(res.data || []);
      } catch {
        setBots([]);
      }
    };
    load();
  }, []);

  const owned = useMemo(
    () => (bots || []).filter((b) => (b.bot_type || "").startsWith("vantaphone-")),
    [bots],
  );
  const allowed = owned.length > 0 || user?.is_admin;

  useEffect(() => {
    if (!allowed) return undefined;
    const onScroll = () => {
      let current = SECTIONS[0].id;
      SECTIONS.forEach((s) => {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top < 140) current = s.id;
      });
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [allowed]);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />
      <main className="pt-24 pb-20 max-w-6xl mx-auto px-4 sm:px-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-white/40 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" />Tableau de bord
        </Link>

        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center shrink-0">
            <BookOpen className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Documentation VantaPhone</h1>
            <p className="text-white/40 text-sm mt-1">Installer, configurer et personnaliser le script sur ton serveur FiveM.</p>
            {owned.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {owned.map((b) => (
                  <span key={b.id} className="text-xs px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/25 text-blue-200">
                    {PRODUCT_LABELS[b.bot_type] || b.bot_type_name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {bots === null ? (
          <div className="flex justify-center py-24">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : !allowed ? (
          <div className="max-w-lg mx-auto text-center bg-[#12121a] border border-white/5 rounded-xl p-10">
            <Lock className="w-10 h-10 text-white/30 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Réservée aux clients VantaPhone</h2>
            <p className="text-sm text-white/50 mb-6">La documentation est disponible dès que tu as acheté l'Yphone, la tablette ou le Pack.</p>
            <Link to="/shop" className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm font-medium">Voir la boutique</Link>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8">
            <nav className="lg:w-60 shrink-0">
              <div className="lg:sticky lg:top-24 flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
                {SECTIONS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.id}
                      onClick={() => goTo(s.id)}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm whitespace-nowrap text-left transition-colors ${active === s.id ? "bg-blue-500/15 text-white" : "text-white/50 hover:text-white hover:bg-white/5"}`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />{s.title}
                    </button>
                  );
                })}
              </div>
            </nav>

            <div className="flex-1 min-w-0 space-y-6">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <section key={s.id} id={s.id} className="bg-[#12121a] border border-white/5 rounded-xl p-5 sm:p-7 scroll-mt-24">
                    <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-bold mb-2">
                      <Icon className="w-5 h-5 text-blue-400" />{s.title}
                    </h2>
                    {s.body}
                  </section>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
